import { useState } from 'react';
import { toast } from 'sonner';

import ProductCard from '@/components/ProductCard';
import DeckModal from '@/components/browse/DeckModal';
import { Package } from 'lucide-react';
import RecipientSelectorModal from '@/components/RecipientSelectorModal';
import WishlistSelectorModal from '@/components/WishlistSelectorModal';
export default function DeckCard({
  deck,
  products,
  deckId,
  openDeckId,
  hoveredDeck,
  fannedDeckMobile,
  isMobile,
  onMouseEnter,
  onMouseLeave,
  onClick,
  onCloseDeck,
  onProductClick,
  user
}) {
  const isOpen = openDeckId === deckId;
  const isHovered = hoveredDeck === deckId;
  const isFannedOnMobile = fannedDeckMobile === deckId;
  
  const [showWishlistSelector, setShowWishlistSelector] = useState(false);
  const [showRecipientSelector, setShowRecipientSelector] = useState(false);

  return (
    <div 
      data-deck-id={deckId}
      data-testid={`deck-card-${deckId}`}
      className="relative cursor-pointer group h-[280px] sm:h-[350px] lg:h-[420px]"
      style={{
        borderRadius: '20px',
        padding: '8px',
        background: 'transparent',
        transition: 'all 0.3s ease',
        // Lift the entire deck above neighboring grid cells while its cards
        // are fanned out, without this, the rotated cards extend past the
        // deck's bounding box and get painted under the next product card.
        // Kept below the sticky filter bar (z-40) so a fanned deck near the
        // top of the grid slides *behind* the search/filter section.
        zIndex: (isHovered || isFannedOnMobile || isOpen) ? 20 : 'auto'
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
    >
      {/* Deck Badge */}
      <div 
        className="absolute -top-3 left-4 z-10 px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1"
        style={{
          background: '#FED7A5',
          color: '#20212B',
          boxShadow: '0 2px 6px rgba(0,0,0,0.15)'
        }}
      >
        <Package className="w-3 h-3" />
        Deck of {products.length}
      </div>
      
      {/* Stacked/Fanned Product Cards */}
      {products.map((product, cardIdx) => {
        const totalCards = products.length;
        const fanned = isHovered || isFannedOnMobile;
        // Only offset the stack while fanned. At rest the cards sit perfectly
        // aligned, so the cards behind never peek out the right/bottom edge
        // (that peek was the "cream/peach border" on light-background photos).
        let translateX = fanned ? cardIdx * 3 : 0;
        let translateY = fanned ? cardIdx * 3 : 0;
        let rotation = 0;
        
        if (fanned) {
          const middleIndex = (totalCards - 1) / 2;
          const offset = cardIdx - middleIndex;
          rotation = offset * 8;
        }
        // Progressive depth scrim on the cards behind the front one so any
        // edge that peeks while fanning recedes instead of flashing bright.
        const scrim = cardIdx === 0 ? 0 : Math.min(0.4, cardIdx * 0.22);
        
        return (
          <div
            key={product.id}
            className="absolute inset-0 pointer-events-none"
            style={{
              transform: `translate(${translateX}px, ${translateY}px) rotate(${rotation}deg)`,
              transformOrigin: 'center bottom',
              transition: 'transform 0.3s ease-out',
              zIndex: totalCards - cardIdx
            }}
          >
            <ProductCard product={product} user={user} />
            {scrim > 0 && (
              <div
                className={`absolute inset-0 rounded-2xl pointer-events-none`}
                style={{ backgroundColor: `rgba(32, 33, 43, ${scrim})`, transition: 'background-color 0.3s ease-out' }}
              />
            )}
          </div>
        );
      })}
      
      {/* Deck Modal */}
      <DeckModal
        isOpen={isOpen}
        onClose={onCloseDeck}
        deck={deck}
        products={products}
        user={user}
        onAddToWishlist={() => {
          if (!user) {
            window.dispatchEvent(new CustomEvent('mementa:open-auth'));
            toast.info('Sign in to save gifts');
            return;
          }
          setShowWishlistSelector(true);
        }}
        onAddToKeepsake={() => {
          if (!user) {
            window.dispatchEvent(new CustomEvent('mementa:open-auth'));
            toast.info('Sign in to save gifts');
            return;
          }
          setShowRecipientSelector(true);
        }}
      />
      
      {/* Bulk Wishlist Selector Modal */}
      {showWishlistSelector && (
        <WishlistSelectorModal
          isOpen={showWishlistSelector}
          onClose={() => setShowWishlistSelector(false)}
          productIds={products.map(p => p.id)}
          isBulk={true}
          deckTitle={deck.title}
          onSuccess={() => {
            setShowWishlistSelector(false);
            // Toast is shown in WishlistSelectorModal
          }}
        />
      )}
      
      {/* Bulk Recipient Selector Modal */}
      {showRecipientSelector && (
        <RecipientSelectorModal
          products={products}
          user={user}
          onClose={() => setShowRecipientSelector(false)}
          isBulk={true}
          deckTitle={deck.title}
          deckId={deck.id}
        />
      )}
    </div>
  );
}
